import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Member } from './villages';
import {
  saveEditRequestToSheet,
  fetchEditRequestsFromSheet,
  updateRequestStatusInSheet,
  isAppsScriptConfigured,
} from '@/services/sheetEditSync';

export interface FieldDiff {
  label: string;
  field: keyof Member;
  oldVal: any;
  newVal: any;
}

export interface EditRequest {
  id: string;
  timestamp: string;
  villageId: string;
  villageName: string;
  familyId: string;
  headName: string;
  memberId: string;
  memberName: string;
  originalData: Partial<Member>;
  updatedData: Partial<Member>;
  diffs: FieldDiff[];
  submitterName: string;
  submitterPhone: string;
  note?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewedAt?: string;
  reviewedNote?: string;
}

const STORAGE_PENDING_KEY = 'bhojani_pending_edits';
const STORAGE_OVERRIDES_KEY = 'bhojani_approved_overrides';
const STORAGE_ADMIN_SESSION = 'bhojani_admin_session';

// Admin PIN from env or default
const ADMIN_PIN = import.meta.env.VITE_ADMIN_PIN || '8849';

export const useAdminEditsStore = defineStore('adminEdits', () => {
  const isAdmin = ref<boolean>(initAdminSession());
  const pendingRequests = ref<EditRequest[]>(loadStoredRequests());
  const approvedOverrides = ref<Record<string, Partial<Member>>>(loadStoredOverrides());

  function initAdminSession(): boolean {
    try {
      return sessionStorage.getItem(STORAGE_ADMIN_SESSION) === 'true';
    } catch {
      return false;
    }
  }

  function loadStoredRequests(): EditRequest[] {
    try {
      const data = localStorage.getItem(STORAGE_PENDING_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  function saveRequests() {
    try {
      localStorage.setItem(STORAGE_PENDING_KEY, JSON.stringify(pendingRequests.value));
    } catch (e) {
      console.error('Failed to save edit requests to storage:', e);
    }
  }

  function loadStoredOverrides(): Record<string, Partial<Member>> {
    try {
      const data = localStorage.getItem(STORAGE_OVERRIDES_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  function saveOverrides() {
    try {
      localStorage.setItem(STORAGE_OVERRIDES_KEY, JSON.stringify(approvedOverrides.value));
    } catch (e) {
      console.error('Failed to save approved overrides to storage:', e);
    }
  }

  // --- Admin Authentication ---
  function loginAdmin(pin: string): { success: boolean; message: string } {
    if (!pin || pin.trim() !== ADMIN_PIN.trim()) {
      return { success: false, message: 'ખોટો એડમિન પિન છે (Invalid Admin PIN).' };
    }
    isAdmin.value = true;
    try {
      sessionStorage.setItem(STORAGE_ADMIN_SESSION, 'true');
    } catch {}
    return { success: true, message: 'એડમિન મોડ સફળતાપૂર્વક ચાલુ થયો.' };
  }

  function logoutAdmin() {
    isAdmin.value = false;
    try {
      sessionStorage.removeItem(STORAGE_ADMIN_SESSION);
    } catch {}
  }

  // --- Submit Edit Request by User ---
  function submitEditRequest(payload: {
    villageId: string;
    villageName: string;
    familyId: string;
    headName: string;
    member: Member;
    updatedData: Partial<Member>;
    submitterName: string;
    submitterPhone: string;
    note?: string;
  }): { success: boolean; message: string; id: string } {
    const { member, updatedData } = payload;

    // Detect actual diffs
    const diffs: FieldDiff[] = [];
    const fieldLabels: Record<string, string> = {
      name: 'નામ (Name)',
      nameE: 'અંગ્રેજી નામ (English Name)',
      age: 'ઉંમર (Age)',
      sex: 'જાતિ (Gender)',
      role: 'સંબંધ (Role)',
      occupation: 'વ્યવસાય (Occupation)',
      currentResidence: 'હાલનું સરનામું (Residence)',
      education: 'અભ્યાસ (Education)',
      maternal: 'મોસાળ (Maternal)',
      dob: 'જન્મ તારીખ (DOB)',
      contact: 'મોબાઇલ નંબર (Mobile/Contact)',
    };

    for (const [key, label] of Object.entries(fieldLabels)) {
      const k = key as keyof Member;
      const oldVal = member[k] ?? '';
      const newVal = updatedData[k] ?? '';
      if (String(oldVal).trim() !== String(newVal).trim()) {
        diffs.push({
          label,
          field: k,
          oldVal,
          newVal,
        });
      }
    }

    if (diffs.length === 0) {
      return { success: false, message: 'કોઈ ફેરફાર કરેલ નથી.', id: '' };
    }

    const id = `req-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const newReq: EditRequest = {
      id,
      timestamp: new Date().toLocaleString('gu-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      villageId: payload.villageId,
      villageName: payload.villageName,
      familyId: payload.familyId,
      headName: payload.headName,
      memberId: member.id,
      memberName: member.name,
      originalData: { ...member },
      updatedData: { ...updatedData },
      diffs,
      submitterName: payload.submitterName.trim(),
      submitterPhone: payload.submitterPhone.trim(),
      note: payload.note?.trim(),
      status: 'PENDING',
    };

    pendingRequests.value.unshift(newReq);
    saveRequests();

    // Async save to Google Sheet (Sheet 2)
    saveEditRequestToSheet(newReq).catch((err) => {
      console.warn('Async Google Sheet save failed:', err);
    });

    return {
      success: true,
      message: 'સુધારાની વિનંતી સફળતાપૂર્વક મોકલવામાં આવી છે. એડમિન દ્વારા ચકાસ્યા બાદ અપડેટ થશે.',
      id,
    };
  }

  // --- Admin Approval / Rejection ---
  async function approveEditRequest(id: string): Promise<{ success: boolean; sheetUpdated: boolean }> {
    const req = pendingRequests.value.find((r) => r.id === id);
    if (!req) return { success: false, sheetUpdated: false };

    req.status = 'APPROVED';
    req.reviewedAt = new Date().toLocaleString('gu-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

    saveRequests();

    // Async update to Google Sheet (Sheet 2 & Sheet 1)
    const result = await updateRequestStatusInSheet(req, 'APPROVED');
    return { success: true, sheetUpdated: result.sheetUpdated };
  }

  function rejectEditRequest(id: string, reason?: string): boolean {
    const req = pendingRequests.value.find((r) => r.id === id);
    if (!req) return false;

    req.status = 'REJECTED';
    req.reviewedAt = new Date().toLocaleString('gu-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    if (reason) {
      req.reviewedNote = reason;
    }

    saveRequests();

    // Async update to Google Sheet (Sheet 2)
    updateRequestStatusInSheet(req, 'REJECTED', reason).catch((err) => {
      console.warn('Google Sheet status update failed:', err);
    });

    return true;
  }

  function deleteRequest(id: string): boolean {
    const idx = pendingRequests.value.findIndex((r) => r.id === id);
    if (idx !== -1) {
      pendingRequests.value.splice(idx, 1);
      saveRequests();
      return true;
    }
    return false;
  }

  // Direct edit by admin without request
  function directAdminUpdate(memberId: string, updatedFields: Partial<Member>) {
    approvedOverrides.value[memberId] = {
      ...(approvedOverrides.value[memberId] || {}),
      ...updatedFields,
    };
    saveOverrides();
  }

  const isSyncing = ref(false);

  // Sync edit requests from Google Sheet (Sheet 2)
  async function syncFromSheet() {
    isSyncing.value = true;
    try {
      const sheetReqs = await fetchEditRequestsFromSheet();
      if (sheetReqs && sheetReqs.length > 0) {
        sheetReqs.forEach((sr) => {
          const existingIdx = pendingRequests.value.findIndex((r) => r.id === sr.id);
          if (existingIdx !== -1) {
            pendingRequests.value[existingIdx] = sr;
          } else {
            pendingRequests.value.push(sr);
          }

          // If approved, update overrides in memory and local storage
          if (sr.status === 'APPROVED' && sr.updatedData && Object.keys(sr.updatedData).length > 0) {
            approvedOverrides.value[sr.memberId] = {
              ...(approvedOverrides.value[sr.memberId] || {}),
              ...sr.updatedData,
            };
          }
        });

        saveRequests();
        saveOverrides();
      }
    } catch (e) {
      console.warn('Failed to sync requests from Sheet 2:', e);
    } finally {
      isSyncing.value = false;
    }
  }

  // syncFromSheet is only invoked when Admin opens the admin panel

  const pendingCount = computed(
    () => pendingRequests.value.filter((r) => r.status === 'PENDING').length
  );

  function getMemberOverride(memberId: string): Partial<Member> | undefined {
    return approvedOverrides.value[memberId];
  }

  return {
    isAdmin,
    pendingRequests,
    approvedOverrides,
    pendingCount,
    isSyncing,
    isAppsScriptConfigured,
    loginAdmin,
    logoutAdmin,
    submitEditRequest,
    approveEditRequest,
    rejectEditRequest,
    deleteRequest,
    directAdminUpdate,
    getMemberOverride,
    syncFromSheet,
  };
});
